import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useDebouncedValue } from '#src/app/shared/hooks/debounce';
import { useCountryCode } from '#src/app/shared/hooks/l10n';

import { searchDecisionMakers } from './api/searchDecisionMakers';
import type { DecisionMaker } from './types';

type Result =
	| { readonly status: 'loading' }
	| { readonly status: 'error' }
	| { readonly status: 'loaded'; readonly data: readonly DecisionMaker[] };

// Matches the debounce delay used by the Google Places typeahead
// (usePlaceWithDetailsField) for consistency across search-as-you-type fields.
const DEBOUNCE_DELAY_MS = 350;

export function useDecisionMakerSearch(input: string): Result {
	const countryCode = useCountryCode();
	const utilityContext = useUtilityContext();
	const debouncedInput = useDebouncedValue(input, DEBOUNCE_DELAY_MS);
	// Clearing the input should empty the results immediately rather than waiting out
	// the debounce and showing stale options from the previous query in the meantime.
	const searchQuery = input.trim() === '' ? input : debouncedInput;

	const { data, isPending, isError } = useQuery({
		queryKey: ['decision-makers-search', searchQuery, countryCode],
		queryFn: async () => searchDecisionMakers({ searchQuery, countryCode }, utilityContext),
		placeholderData: keepPreviousData,
	});

	if (isPending) return { status: 'loading' };
	if (isError) return { status: 'error' };
	return { status: 'loaded', data };
}
